import {
  ABILITIES,
  CLIENT_MESSAGE,
  ENEMY_ARCHETYPES,
  NETWORK,
  PLAYER_SPAWN_POINTS,
  SERVER_MESSAGE,
  type AnimationState,
  type ChatBroadcastMessage,
  type ChatSendMessage,
  type CombatFeedbackMessage,
  type LevelUpMessage,
} from "@mucki/shared";
import * as THREE from "three";
import { createThirdPersonCamera } from "./camera/thirdPerson.js";
import { createCatMesh } from "./entities/catMesh.js";
import { resolveAbilityPress } from "./input/abilities.js";
import { createKeyboard } from "./input/keyboard.js";
import { createPointerLook } from "./input/pointer.js";
import { createAbilitySender } from "./network/abilitySender.js";
import { ZoneUnreachableError, connectToZone, watchConnection } from "./network/connection.js";
import { createInputSender } from "./network/inputSender.js";
import { createStateSync } from "./network/stateSync.js";
import { addLighting } from "./scenes/lighting.js";
import { buildZone } from "./scenes/zone.js";
import {
  animationStateFor,
  applyServerCorrection,
  createMovementState,
  stepMovement,
} from "./systems/localMovement.js";
import {
  createCamera,
  createRenderer,
  startRenderLoop,
  syncRendererToCanvas,
} from "./systems/renderer.js";
import { installAssetErrorHandling } from "./systems/assetLoading.js";
import { selectTarget } from "./systems/targeting.js";
import { createConnectionStatus, showFatalError } from "./ui/connectionStatus.js";
import { createChat } from "./ui/chat.js";
import "./ui/chat.css";
import { createDeathOverlay } from "./ui/deathOverlay.js";
import { createHud } from "./ui/hud.js";
import "./ui/hud.css";
import { createLevelUpNotice } from "./ui/levelUpNotice.js";
import { askForName } from "./ui/nameEntry.js";
import { projectToScreen } from "./ui/nameplate.js";

const canvas = document.getElementById("game") as HTMLCanvasElement | null;
if (!canvas) throw new Error("canvas #game is missing from index.html");

installAssetErrorHandling();

const renderer = createRenderer(canvas);
const camera = createCamera();
const scene = new THREE.Scene();
addLighting(scene);
const zone = buildZone(scene);

const keyboard = createKeyboard();
const pointerLook = createPointerLook(canvas);
const thirdPerson = createThirdPersonCamera(camera);

const name = await askForName();
const connectionStatus = createConnectionStatus();

let room;
try {
  room = await connectToZone(name);
} catch (error) {
  const detail =
    error instanceof ZoneUnreachableError
      ? error.message
      : "The zone refused the connection. Check the server log and reload the page.";
  connectionStatus.set("disconnected");
  showFatalError("Cannot reach the zone", detail);
  throw error;
}

watchConnection(room, (state) => connectionStatus.set(state));

const stateSync = createStateSync(room, scene);
const inputSender = createInputSender(room);
const abilitySender = createAbilitySender(room);

const hud = createHud();
const deathOverlay = createDeathOverlay();
const levelUpNotice = createLevelUpNotice();

const chat = createChat({
  onSend: (text) => room.send(CLIENT_MESSAGE.chat, { text } satisfies ChatSendMessage),
  onFocusChange: (focused) => {
    // Typing must not walk the cat, and the mouse belongs to the field.
    if (focused) pointerLook.releaseLock();
  },
});

room.onMessage(SERVER_MESSAGE.chat, (message: ChatBroadcastMessage) => chat.append(message));

/** The widest targeted ability decides what the target frame shows. */
const FRAME_ABILITY = Object.values(ABILITIES)
  .filter((ability) => ability.requiresTarget)
  .reduce((widest, ability) => (ability.range > widest.range ? ability : widest));

/** Combat states are server-owned; movement states are predicted locally. */
const SERVER_OWNED: ReadonlySet<AnimationState> = new Set(["attacking", "hurt", "dead"]);

const spawn = PLAYER_SPAWN_POINTS[0] ?? { x: 0, z: 0 };
const localCat = createCatMesh();
scene.add(localCat.group);
let movement = createMovementState(spawn.x, spawn.z);

function worldPositionOf(id: string): THREE.Vector3Like | undefined {
  const enemy = stateSync.findEnemy(id);
  if (enemy) return { x: enemy.x, y: enemy.y + 1.4, z: enemy.z };
  const player = stateSync.findPlayer(id);
  if (player) return { x: player.x, y: player.y + 1.6, z: player.z };
  return undefined;
}

room.onMessage(SERVER_MESSAGE.levelUp, (message: LevelUpMessage) => {
  if (message.sessionId === room.sessionId) levelUpNotice.show(message);
});

room.onMessage(SERVER_MESSAGE.combat, (message: CombatFeedbackMessage) => {
  if (message.sourceId === room.sessionId && message.abilityId) {
    hud.startCooldown(message.abilityId, Date.now());
  }
  const world = worldPositionOf(message.targetId);
  if (world) hud.spawnCombatText(message, projectToScreen(world, camera, canvas));
});

let elapsed = 0;
startRenderLoop((delta) => {
  elapsed += delta;
  const now = Date.now();
  syncRendererToCanvas(renderer, camera);
  zone.update(elapsed);

  const localState = stateSync.localPlayer();
  const dead = localState?.dead ?? false;
  // Input is suspended by the server's dead flag, not by a local guess, and
  // while the chat field has focus.
  keyboard.setEnabled(!dead && !chat.isFocused());
  deathOverlay.update(dead);

  movement = stepMovement(movement, keyboard.movement(), pointerLook.yaw, delta);
  if (localState) {
    movement = applyServerCorrection(
      movement,
      localState,
      NETWORK.correctionThreshold,
      NETWORK.correctionRate,
      delta,
    );
  }

  const predicted = animationStateFor(movement);
  const animation =
    localState && SERVER_OWNED.has(localState.animation) ? localState.animation : predicted;

  localCat.group.position.set(movement.x, movement.y, movement.z);
  localCat.group.rotation.y = movement.rotationY;
  localCat.setAnimationState(animation);
  localCat.update(delta);

  for (const hotkey of keyboard.takeHotkeys()) {
    const ability = resolveAbilityPress(hotkey, movement, stateSync.enemies());
    if (ability) abilitySender.send(ability);
  }

  const targetId = selectTarget(movement, stateSync.enemies(), FRAME_ABILITY);
  const targetEnemy = targetId ? stateSync.findEnemy(targetId) : undefined;

  hud.update({
    player: localState,
    target: targetEnemy
      ? {
          name: ENEMY_ARCHETYPES[targetEnemy.archetype].name,
          health: targetEnemy.health,
          maxHealth: targetEnemy.maxHealth,
        }
      : undefined,
    now,
  });

  inputSender.update(delta, movement, predicted);
  stateSync.update(delta, camera, canvas);

  thirdPerson.update(localCat.group.position, pointerLook.yaw, pointerLook.pitch, delta);
  renderer.render(scene, camera);
});
